import {useGetPatientTherapySequence, useGetPatientTherapySequenceActivities} from '@/modules/patients-therapies/api'
import {EventLog, PatientTherapySequence, User} from '@cypro/engine/schema'
import {createContext, PropsWithChildren, useContext} from 'react'
import {useGetUser} from '@/modules/users/api'

interface State {
  sequence: PatientTherapySequence
  prescriber?: User
  responsibleDoctor?: User
  validator?: User
  pharmacistValidator?: User
  administeredBy?: User
  returnedBy?: User
  cancelledBy?: User
  discardedBy?: User
  activities?: EventLog[]
}

interface Props extends PropsWithChildren {
  sequenceId: string
}

const PatientTherapySequenceContext = createContext<State>({} as State)

export function PatientTherapySequenceProvider({children, sequenceId}: Props) {
  const [sequence] = useGetPatientTherapySequence(sequenceId)
  const medicineIds = (sequence?.medicine ?? []).map(m => m.id)
  const [activities] = useGetPatientTherapySequenceActivities(sequenceId, medicineIds)
  const [prescriber] = useGetUser(sequence?.createdBy)
  const [responsibleDoctor] = useGetUser(sequence?.responsibleDoctorId)
  const [validator] = useGetUser(sequence?.doctorValidatedBy)
  const [pharmacistValidator] = useGetUser(sequence?.pharmacistValidatedBy)
  const [administeredBy] = useGetUser(sequence?.administeredBy)
  const [returnedBy] = useGetUser(sequence?.returnedBy)
  const [cancelledBy] = useGetUser(sequence?.cancelledBy)
  const [discardedBy] = useGetUser(sequence?.discardedBy)

  if (!sequence) throw new Error('Sequence could not be found')

  return (
    <PatientTherapySequenceContext.Provider
      key={[
        sequence?.status,
        sequence?.pharmacistValidatedAt,
        sequence?.doctorValidatedAt,
        sequence?.patientArrivedAt,
        sequence?.administeredAt,
        sequence?.returnedAt,
        sequence?.cancelledAt,
        sequence?.discardedAt,
      ].join('-')}
      value={{
        sequence,
        activities,
        prescriber,
        responsibleDoctor,
        validator,
        pharmacistValidator,
        administeredBy,
        returnedBy,
        cancelledBy,
        discardedBy,
      }}
    >
      {children}
    </PatientTherapySequenceContext.Provider>
  )
}

export function usePatientTherapySequenceContext() {
  return useContext(PatientTherapySequenceContext)
}
